import { z } from "zod";
import { MissionCommandSchema } from "./mission-api.js";
import { appContract } from "./rpc.js";

/** Explicit capabilities, not an unrestricted RPC proxy. Authentication and secret flows stay native. */
export const PLATFORM_ACTIONS: Record<string, "read" | "action" | "manage"> = Object.fromEntries([
  ...[
    "spaces.list",
    "workspace.preferences",
    "workspace.activity",
    "workspace.computers",
    "workspace.search",
    "workspace.resultGuide",
    "workspace.resultSources",
    "workspace.system",
    "models.list",
    "models.credentials",
    "models.backups",
    "bots.list",
    "bots.get",
    "bots.listArchived",
    "bots.coding",
    "groups.list",
    "groups.get",
    "groups.listArchived",
    "botSections.list",
    "threads.get",
    "threads.messages",
    "computer.status",
    "computer.updates",
    "memory.list",
    "memory.exportMarkdown",
    "routines.list",
    "routines.history",
    "scratchpad.list",
    "missions.list",
    "missions.get",
    "missions.prepare",
    "missions.history",
    "missions.changes",
    "missions.repositorySnapshot",
    "missions.repositoryFile",
    "missions.checkLog",
    "projects.list",
    "boards.list",
    "boards.get",
    "tickets.list",
    "tickets.get",
    "tickets.comments",
    "agentSkills.list",
    "agentSkills.get",
    "capabilities.list",
    "capabilities.catalogSearch",
    "connections.list",
    "connections.catalog",
    "connections.tools",
    "mcp.servers.list",
    "mcp.assignments.list",
    "library.groups",
    "artifacts.list",
    "artifacts.listSpace",
    "artifacts.listVersions",
    "artifacts.getById",
    "usage.summary",
    "search.query",
    "runs.list",
    "runs.activity",
    "voice.status",
    "voice.catalog",
    "voice.voices",
    "sharedBots.list",
    "sharedBots.resources",
  ].map((path) => [path, "read"]),
  ...[
    "workspace.savePreferences",
    "spaces.rename",
    "bots.reorder",
    "bots.restore",
    "groups.restore",
    "botSections.create",
    "botSections.update",
    "threads.send",
    "threads.followUp",
    "threads.stop",
    "threads.react",
    "threads.markRead",
    "threads.markUnread",
    "computer.boot",
    "computer.stop",
    "memory.update",
    "routines.update",
    "routines.testRun",
    "scratchpad.create",
    "scratchpad.update",
    "missions.checkpoint",
    "missions.capture",
    "missions.collectRepository",
    "missions.collectPreview",
    "projects.save",
    "boards.rename",
    "tickets.create",
    "tickets.update",
    "tickets.move",
    "tickets.comment",
    "library.saveGroup",
    "library.change",
    "artifacts.create",
    "artifacts.saveVersion",
    "connections.rename",
    "devices.rename",
  ].map((path) => [path, "action"]),
  ...[
    "missions.create",
    "missions.command",
    "spaces.create",
    "bots.create",
    "bots.duplicate",
    "bots.update",
    "bots.archive",
    "bots.setComputer",
    "groups.create",
    "groups.update",
    "groups.archive",
    "routines.create",
    "routines.remove",
    "agentSkills.create",
    "agentSkills.update",
    "models.setDefault",
    "models.setBackups",
    "voice.setVoice",
    "voice.setSpeechModel",
  ].map((path) => [path, "manage"]),
]);

export const PLATFORM_MISSION_COMMANDS = [
  "prepare_plan",
  "direction",
  "coding_followup",
  "plan",
  "status",
  "assignment",
  "sources",
  "decision",
] as const;
export function isPlatformMissionCommand(action: unknown): boolean {
  return PLATFORM_MISSION_COMMANDS.some((allowed) => allowed === action);
}
const platformMissionSchema = z.union(
  MissionCommandSchema.options.filter((option) =>
    isPlatformMissionCommand(option.shape.action.value),
  ),
);

export const PlatformRequestSchema = z.object({
  kind: z.enum(["catalog", "read", "action", "manage"]),
  operation: z.string().max(100).optional(),
  category: z.string().max(40).optional(),
  spaceId: z.string().max(200).optional(),
  input: z.record(z.string(), z.unknown()).optional(),
});
export const PlatformEnvelopeSchema = PlatformRequestSchema.extend({
  runId: z.string().min(1),
  effectKey: z.string().min(1).max(500),
});
export type PlatformRequest = z.infer<typeof PlatformRequestSchema>;

export function platformCatalog(category?: string) {
  const entries = Object.entries(PLATFORM_ACTIONS).filter(
    ([path]) => !category || path.split(".")[0] === category,
  );
  if (!category)
    return {
      categories: [...new Set(entries.map(([path]) => path.split(".")[0]))],
      operations: entries.map(([operation, kind]) => ({ operation, kind })),
    };
  return {
    operations: entries.map(([operation, kind]) => {
      let procedure: unknown = appContract;
      for (const part of operation.split("."))
        procedure = (procedure as Record<string, unknown>)[part];
      const schema =
        operation === "missions.command"
          ? platformMissionSchema
          : (procedure as { "~orpc": { inputSchema?: z.ZodType } })?.["~orpc"].inputSchema;
      if (!procedure) throw new Error(`Unknown platform operation ${operation}`);
      return {
        operation,
        kind,
        inputSchema: schema
          ? z.toJSONSchema(schema, { io: "input", unrepresentable: "any" })
          : { type: "object", properties: {}, additionalProperties: false },
      };
    }),
  };
}
